// File: client/src/variants/power/usePowerRuntime.js
//
// Power Chess runtime. Each piece type has an RPG ability with a cooldown (in the
// owner's turns). The player ACTIVATES an ability from the ability bar; move-abilities
// are armed (they add legal moves), effect-abilities apply immediately. Everything
// runs through the variant contract — no engine changes:
//   - 'move' abilities (Sprint, Leap) add legal moves via transformLegalMoves.
//   - Fortify / Cleanse grant capture-immunity to a rook / bishop (enforced by
//     filtering enemy captures of the protected square).
//   - Blitz keeps the turn open for a second move.
//   - Royal Command resets the owner's cooldowns.

import React, { useRef, useState, useCallback } from 'react';
import { Zap } from 'lucide-react';
import {
  ABILITIES, initCooldowns, isReady, trigger, tick, resetAll,
} from './abilities';

export function usePowerRuntime({ config, resumePayload }) {
  const isVsAI = config.opponent === 'ai';
  const humanColor = 'white';

  const cooldownsRef = useRef(resumePayload ? resumePayload.cooldowns : initCooldowns());
  const armedRef = useRef(null);   // move-ability id armed for the current turn (or 'blitz')
  const immunityRef = useRef(null); // { color, square } capture-immunity for this turn cycle
  const currentColorRef = useRef('white');
  const lastStateRef = useRef(null);

  const [, force] = useState(0);
  const rerender = () => force(n => n + 1);

  const lastBoard = () => (lastStateRef.current ? lastStateRef.current.board : null);
  function findPiece(board, letter) {
    if (!board) return null;
    for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) if (board[r][c] === letter) return { row: r, col: c };
    return null;
  }

  const activateAbility = useCallback((color, abilityId) => {
    if (color !== currentColorRef.current) return;
    if (!isReady(cooldownsRef.current, color, abilityId)) return;
    const ability = Object.values(ABILITIES).find(a => a.id === abilityId);
    if (!ability) return;

    if (ability.kind === 'move') {
      armedRef.current = armedRef.current === abilityId ? null : abilityId; // toggle
      rerender();
      return;
    }

    // Effect abilities.
    if (ability.id === 'royal_command') {
      cooldownsRef.current = trigger(resetAll(cooldownsRef.current, color), color, 'royal_command');
    } else if (ability.id === 'fortify' || ability.id === 'cleanse') {
      const letter = ability.id === 'fortify'
        ? (color === 'white' ? 'R' : 'r')
        : (color === 'white' ? 'B' : 'b');
      const sq = findPiece(lastBoard(), letter);
      if (sq) immunityRef.current = { color, square: sq };
      cooldownsRef.current = trigger(cooldownsRef.current, color, ability.id);
    } else if (ability.id === 'blitz') {
      armedRef.current = 'blitz';
      cooldownsRef.current = trigger(cooldownsRef.current, color, 'blitz');
    }
    rerender();
  }, []);

  const moveMiddleware = {
    beforeMove(gameState, from, to) {
      lastStateRef.current = gameState;
      const im = immunityRef.current;
      if (im && to.row === im.square.row && to.col === im.square.col && gameState.turn !== im.color) {
        return { allow: false, reason: 'That piece is protected this turn.' };
      }
      return { allow: true };
    },

    transformLegalMoves(gameState, from, engineMoves) {
      let moves = [...engineMoves];
      const activeColor = gameState.turn;
      const piece = gameState.board[from.row][from.col];
      if (!piece) return moves;
      const pieceColor = piece === piece.toUpperCase() ? 'white' : 'black';

      const armed = armedRef.current;
      if (armed && armed !== 'blitz' && pieceColor === activeColor) {
        const ability = Object.values(ABILITIES).find(a => a.id === armed);
        if (ability && ability.kind === 'move' && piece.toLowerCase() === ability.piece) {
          const extra = ability.extraMoves(gameState.board, from, pieceColor === 'white') || [];
          const seen = new Set(moves.map(m => `${m.row},${m.col}`));
          extra.forEach(m => { if (!seen.has(`${m.row},${m.col}`)) moves.push(m); });
        }
      }

      const im = immunityRef.current;
      if (im && pieceColor !== im.color) {
        moves = moves.filter(m => !(m.row === im.square.row && m.col === im.square.col));
      }
      return moves;
    },

    afterMove(newState, from, to) {
      lastStateRef.current = newState;
      const moverColor = newState.turn;

      // Put a used move-ability on cooldown.
      const armed = armedRef.current;
      if (armed && armed !== 'blitz') {
        const ability = Object.values(ABILITIES).find(a => a.id === armed);
        const movedPiece = newState.board[to.row][to.col];
        if (ability && movedPiece && movedPiece.toLowerCase() === ability.piece) {
          cooldownsRef.current = trigger(cooldownsRef.current, moverColor, armed);
        }
      }

      let keepOpen = false;
      if (armedRef.current === 'blitz') {
        armedRef.current = null;
        keepOpen = true;
      } else {
        armedRef.current = null;
      }

      if (!keepOpen) {
        cooldownsRef.current = tick(cooldownsRef.current, moverColor);
        if (immunityRef.current && immunityRef.current.color === moverColor) {
          immunityRef.current = null; // immunity lasts until the owner's next move
        }
        currentColorRef.current = moverColor === 'white' ? 'black' : 'white';
      }
      rerender();
      return { turnEnded: !keepOpen };
    },
  };

  const getPersistencePayload = useCallback(() => ({ cooldowns: cooldownsRef.current }), []);

  const renderOverlay = (slot) => {
    if (slot !== 'bottom') return null;
    const color = currentColorRef.current;
    const canControl = !isVsAI || color === humanColor;
    return (
      <div className="w-full max-w-2xl mt-4 px-4">
        <p className="text-slate-300 text-sm font-medium mb-2 flex items-center gap-2">
          <Zap size={16} className="text-yellow-400" /> Abilities — {color === 'white' ? '⚪ White' : '⚫ Black'}
          {!canControl && <span className="text-xs text-slate-500">(opponent’s turn)</span>}
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {Object.values(ABILITIES).map(a => {
            const cd = cooldownsRef.current[color][a.id] || 0;
            const ready = cd <= 0;
            const armed = armedRef.current === a.id;
            return (
              <button
                key={a.id}
                disabled={!canControl || !ready}
                onClick={() => activateAbility(color, a.id)}
                title={a.describe}
                className={`p-2 rounded-lg border-2 text-center transition ${
                  armed
                    ? 'bg-yellow-600 border-yellow-300 text-white'
                    : ready
                      ? 'bg-slate-700 border-slate-500 text-slate-100 hover:border-yellow-400'
                      : 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                }`}
              >
                <div className="text-lg">{a.icon}</div>
                <div className="text-[10px] font-semibold">{a.name}</div>
                <div className="text-[9px] opacity-75">{ready ? 'Ready' : `CD ${cd}`}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return { moveMiddleware, getPersistencePayload, renderOverlay };
}
